import React, { FC, useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  MenuItem,
  TextField,
  Typography,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import axiosInstant from '../../api';
import { createBrandSOW, updateBrandSOW, pricingPackages } from '../../api/endpoints';
import { InputField } from '../pro/applicationForm/input';
import { CustomFieldLabel } from '../../components/fields/CustomFieldLabel/CustomFieldLabel';
import InlineBrandSelector from '../videoProduction/components/InlineBrandSelector';
import { useUser } from '../../hooks/useUser';
import { isBrand } from '../../utils';
import { formatCurrency } from '../../utils/formatCurrency';

interface PackageOption {
  id: number;
  key: string;
  display_name: string;
  sell_price: string | number;
  max_skus: number;
  term_months: number | null;
  per_video_price: string | number | null;
  payment_schedule: string | null;
  active: boolean;
}

interface SOWFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void | Promise<void>;
  mode?: 'create' | 'edit';
  sowId?: number;
  initialName?: string;
  initialUrl?: string;
  initialBrand?: any | null;
  initialPackageId?: number | null;
  initialStartDate?: string | null;
  initialEndDate?: string | null;
}

interface SOWFormValues {
  name: string;
  url: string;
  brand: any | null;
  package_id: number | '';
  start_date: string;
  end_date: string;
}

const addMonths = (dateStr: string, months: number): string => {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required('SOW name is required'),
  url: Yup.string().url('Please enter a valid URL').required('URL is required'),
  brand: Yup.mixed().required('Brand is required'),
});

export const SOWFormModal: FC<SOWFormModalProps> = ({
  open,
  onClose,
  onSuccess,
  mode = 'create',
  sowId,
  initialName = '',
  initialUrl = '',
  initialBrand = null,
  initialPackageId = null,
  initialStartDate = null,
  initialEndDate = null,
}) => {
  const { role, brand: userBrand } = useUser();
  const isBrandRole = isBrand(role);
  const userBrandData = userBrand?.[0] || null;

  const effectiveBrand = isBrandRole ? (initialBrand || userBrandData) : initialBrand;

  const [selectedBrand, setSelectedBrand] = useState<any>(effectiveBrand);
  const [packages, setPackages] = useState<PackageOption[]>([]);

  // Package catalog for the select — internal users only (brands never see
  // pricing or packages).
  useEffect(() => {
    if (!open || isBrandRole) return;
    axiosInstant
      .get(pricingPackages)
      .then((res) => setPackages(res.data ?? []))
      .catch(() => setPackages([]));
  }, [open, isBrandRole]);

  const initialValues: SOWFormValues = {
    name: initialName,
    url: initialUrl,
    brand: effectiveBrand,
    package_id: initialPackageId ?? '',
    start_date: initialStartDate ?? '',
    end_date: initialEndDate ?? '',
  };

  const handleBrandChange = useCallback(
    (setFieldValue: (field: string, value: any) => void) =>
      (field: string, value: any) => {
        if (field === 'brands') {
          const brandValue = value?.[0] || null;
          setFieldValue('brand', brandValue);
          setSelectedBrand(brandValue);
        } else if (field === 'brand') {
          const brandValue = value === '' || value === null || value === undefined ? null : value;
          if (brandValue === null || typeof brandValue !== 'string') {
            setFieldValue('brand', brandValue);
            setSelectedBrand(brandValue);
          }
        } else {
          setFieldValue(field, value);
        }
      },
    []
  );

  const handleSubmit = async (values: SOWFormValues, actions: any) => {
    const brandId = values.brand?.id ?? selectedBrand?.id;
    if (!brandId) {
      toast.error('Brand is required');
      return;
    }

    // Brand users never see the package fields — don't send them, so a brand
    // edit can't clear an engagement an internal user attached.
    const packageFields = isBrandRole
      ? {}
      : {
          package_id: values.package_id === '' ? null : Number(values.package_id),
          start_date: values.start_date || null,
          end_date: values.end_date || null,
        };

    try {
      if (mode === 'create') {
        await axiosInstant.post(createBrandSOW(), {
          name: values.name,
          url: values.url,
          brand_id: brandId,
          ...packageFields,
        });
        toast.success('SOW created successfully.');
      } else if (mode === 'edit' && sowId) {
        await axiosInstant.patch(updateBrandSOW(sowId), {
          name: values.name,
          url: values.url,
          brand_id: brandId,
          ...packageFields,
        });
        toast.success('SOW updated successfully.');
      }
      if (onSuccess) await onSuccess();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.error ?? err?.message ?? 'Failed to save SOW';
      toast.error(msg);
    } finally {
      actions.setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box className="flex justify-between items-center">
          <Typography variant="h5">{mode === 'edit' ? 'Edit SOW' : 'New SOW'}</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, setFieldValue, touched, errors, values }) => (
          <Form className="overflow-y-auto">
            <DialogContent className="border-y border-border-token">
              <Box display="flex" flexDirection="column" gap={3}>
                <InputField
                  name="name"
                  label="SOW Name"
                  placeholder="Enter SOW name"
                  required
                />

                {!isBrandRole && (
                  <div>
                    <div className="flex items-center gap-[6px] mb-1">
                      <CustomFieldLabel required>Brand</CustomFieldLabel>
                    </div>
                    <InlineBrandSelector
                      setFieldValue={handleBrandChange(setFieldValue)}
                      isRMN={false}
                      showRmnToggle={false}
                      selectedBrand={selectedBrand}
                      setSelectedBrand={(brand: any) => {
                        setSelectedBrand(brand);
                        setFieldValue('brand', brand);
                      }}
                      disabled={false}
                      filterRmn={undefined}
                      allowAdd={false}
                    />
                    {touched.brand && errors.brand && (
                      <Typography color="error" variant="caption">
                        {String(errors.brand)}
                      </Typography>
                    )}
                  </div>
                )}

                <InputField
                  name="url"
                  label="URL"
                  placeholder="https://example.com/sow-document"
                  required
                />

                {!isBrandRole && (
                  <>
                    <div>
                      <div className="flex items-center gap-[6px] mb-1">
                        <CustomFieldLabel>Package</CustomFieldLabel>
                      </div>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={values.package_id}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const pkgId = raw === '' ? '' : Number(raw);
                          setFieldValue('package_id', pkgId);
                          // Convenience: fill a blank end date from the term.
                          const pkg = packages.find((p) => p.id === pkgId);
                          if (pkg?.term_months && values.start_date && !values.end_date) {
                            setFieldValue('end_date', addMonths(values.start_date, pkg.term_months));
                          }
                        }}
                      >
                        <MenuItem value="">
                          <em>None — à-la-carte pricing</em>
                        </MenuItem>
                        {packages
                          .filter((p) => p.active || p.id === values.package_id)
                          .map((p) => (
                            <MenuItem key={p.id} value={p.id}>
                              {p.display_name} — {formatCurrency(p.sell_price)}
                            </MenuItem>
                          ))}
                      </TextField>
                      {(() => {
                        const pkg = packages.find((p) => p.id === values.package_id);
                        if (!pkg) return null;
                        return (
                          <Box
                            sx={{
                              mt: 1,
                              p: 1.5,
                              borderRadius: 1.5,
                              bgcolor: 'var(--tint)',
                              border: '1px solid var(--accent)',
                            }}
                          >
                            <Typography sx={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6 }}>
                              <b>{pkg.display_name}</b> — billed flat{' '}
                              <b>{formatCurrency(pkg.sell_price)}</b> · up to {pkg.max_skus} SKU
                              {pkg.max_skus === 1 ? '' : 's'} ·{' '}
                              {pkg.term_months ? `${pkg.term_months}-month term` : 'single project'}
                              {pkg.payment_schedule ? ` · ${pkg.payment_schedule}` : ''}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: 'var(--accent-text)', mt: 0.5 }}>
                              Terms are snapshotted onto this SOW when saved — later package edits
                              won&apos;t change this engagement. Re-select the package to refresh.
                            </Typography>
                          </Box>
                        );
                      })()}
                    </div>

                    <Box display="flex" gap={2}>
                      <div className="flex-1">
                        <div className="flex items-center gap-[6px] mb-1">
                          <CustomFieldLabel>Start Date</CustomFieldLabel>
                        </div>
                        <TextField
                          fullWidth
                          size="small"
                          type="date"
                          value={values.start_date}
                          onChange={(e) => {
                            setFieldValue('start_date', e.target.value);
                            const pkg = packages.find((p) => p.id === values.package_id);
                            if (pkg?.term_months && e.target.value && !values.end_date) {
                              setFieldValue('end_date', addMonths(e.target.value, pkg.term_months));
                            }
                          }}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-[6px] mb-1">
                          <CustomFieldLabel>End Date</CustomFieldLabel>
                        </div>
                        <TextField
                          fullWidth
                          size="small"
                          type="date"
                          value={values.end_date}
                          onChange={(e) => setFieldValue('end_date', e.target.value)}
                        />
                      </div>
                    </Box>
                  </>
                )}
              </Box>
            </DialogContent>

            <DialogActions className="!px-6 !py-4">
              <Button onClick={onClose} sx={{ textTransform: 'capitalize' }}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                sx={{ textTransform: 'capitalize' }}
              >
                {isSubmitting
                  ? mode === 'edit' ? 'Saving...' : 'Creating...'
                  : mode === 'edit' ? 'Save Changes' : 'Create SOW'}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};
